import { useState } from "react";
import { Box, Stack, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import ErrorOutlineRoundedIcon from "@material-ui/icons/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@material-ui/icons/WarningAmberRounded";
import { groupMatchReasons, isCriticalMissingFeature } from "./table/matchRows";
import { exactHebrew } from "../crm/crmFormat";
import type { ListingMatchReason } from "../../../@types/sharedSchema";

/**
 * Why this customer was matched to this listing, as a sentence a realtor can act on — the panel
 * behind each row's "פרטים נוספים".
 *
 * It replaces the per-criterion chips (2026-09-22, "all the chips are too much"): up to nine of
 * them rode every row in four colours, and the realtor still had to work out which ones were the
 * problem. The three sections answer the three questions in the order they matter — what is
 * DIFFERENT from what the customer asked for, what the listing does not SAY, and only then what
 * fits. The labels themselves come from the server ready to display (listingMatchRules.ts), so
 * this screen, the MCP tools and a colleague's row all say the same thing.
 */

/**
 * "+2" after a reason whose label names fewer values than the customer actually stated
 * (`requestedValues` — today the property types). Clicking it spells the whole list out inline.
 *
 * Reported 2026-09-22 with a screenshot: a customer had ticked דופלקס, דירה מחולקת and דירת גן,
 * and the panel said `דירה · מבוקש: דירה מחולקת` — which reads as "that is all they wanted". The
 * label stays narrow (seven type names in one line is unreadable), so the rest live behind this.
 */
function RequestedValuesToggle({ reason }: { reason: ListingMatchReason }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const values = reason.requestedValues ?? [];
  // Only worth a control when the label is actually hiding something: count the stated values the
  // label does not already spell out.
  const hidden = values.filter((value) => !reason.label.includes(value));
  if (hidden.length === 0) return null;

  return (
    <>
      <Box
        component="span"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        sx={{
          display: "inline-block",
          mx: 0.5,
          px: 0.75,
          borderRadius: 1,
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 700,
          color: "primary.main",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.22) }
        }}
      >
        {/* ltr: in an RTL line a bare "+2" renders as "2+". */}
        <Box component="span" dir="ltr">
          {open ? "−" : `+${hidden.length}`}
        </Box>
      </Box>
      {open && (
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
          {`הלקוח ביקש: ${values.join(", ")}`}
        </Typography>
      )}
    </>
  );
}

type SectionProps = {
  title: string;
  hint: string;
  reasons: ListingMatchReason[];
  color: "warning" | "success" | "error" | "text";
  icon: JSX.Element;
  /** Narrow CONTAINER (not a narrow window) — see the note on `MatchReasonDetails`. */
  compact?: boolean;
};

function Section({ title, hint, reasons, color, icon, compact }: SectionProps): JSX.Element | null {
  if (reasons.length === 0) return null;
  const main = color === "text" ? "text.secondary" : `${color}.dark`;
  return (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
      {/* `flexWrap` + `minWidth: 0`: at 256px (a phone, inside the listing dialog) the title and the
          hint cannot share a line, and a nowrap row here pushed the whole section wider than the card. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        flexWrap="wrap"
        sx={{ color: main, minWidth: 0 }}
      >
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>
          {title}
        </Typography>
        {/* The hint restates the title for someone reading slowly; at 256px it is the first thing
            that should go rather than the first thing that overflows. */}
        {!compact && (
          <Typography variant="caption" color="text.disabled" noWrap>
            · {hint}
          </Typography>
        )}
      </Stack>
      {/*
        LOGICAL padding, never physical. This was `pr: 3, pl: 0` — and stylis-plugin-rtl mirrors
        PHYSICAL properties (the repo states this at `crmFormat.ts:16-19` and `CrmPage.tsx:248-250`),
        so under RTL it shipped as `padding-left: 24px` and the list had ZERO inline-start padding.
        With `list-style-position: outside` the disc markers then render at ≈ −18px — outside the
        content box, where the mobile Card's `overflow: hidden` shaves them. That is the whole of the
        "on mobile פרטים נוספים is cut" report. `paddingInlineStart` is direction-aware natively and
        stylis leaves it alone.
      */}
      <Stack
        component="ul"
        spacing={0.25}
        sx={{ m: 0, paddingInlineStart: compact ? 2 : 2.5, paddingInlineEnd: 0, listStyle: "disc" }}
      >
        {reasons.map((reason) => (
          <Typography
            key={`${reason.criterion}:${reason.label}`}
            component="li"
            variant="body2"
            // A ממ"ד the customer asked for that the listing does not mark is the one gap a realtor
            // must not miss (product decision 2026-09-18) — it stays red here too.
            sx={{
              color: reason.status === "mismatch" || isCriticalMissingFeature(reason) ? "error.main" : "text.primary",
              fontWeight: reason.status === "mismatch" || isCriticalMissingFeature(reason) ? 600 : 400,
              // A server label can be long ("חיפה · נווה שאנן · מבוקש: קרית אליעזר, נווה שאנן") and
              // holds no spaces at the point it needs to break. Wrap it rather than let it push the
              // list wider than the card.
              minWidth: 0,
              overflowWrap: "anywhere"
            }}
          >
            {reason.label}
            <RequestedValuesToggle reason={reason} />
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
}

export function MatchReasonDetails({
  reasons,
  contactName,
  compact,
  createdTime,
  updatedTime
}: {
  reasons?: ListingMatchReason[];
  /** Named in the warning so a realtor scanning several open rows knows whose card is thin. */
  contactName?: string;
  /** Narrow CONTAINER — not a narrow window. See `useContainerWidth`. */
  compact?: boolean;
  /** ISO. The row shows these RELATIVELY; expanded, they are exact so two rows can be compared. */
  createdTime?: string | null;
  updatedTime?: string | null;
}): JSX.Element {
  const groups = groupMatchReasons(reasons);
  const nothingToSay = groups.fits.length === 0 && groups.differs.length === 0 && groups.missing.length === 0;

  return (
    <Stack spacing={1.75} sx={{ py: 1.5 }}>
      {groups.dataDepth && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            p: 1.25,
            borderRadius: 1,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
            color: "warning.dark"
          }}
        >
          <WarningAmberRoundedIcon fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{groups.dataDepth.label}</Typography>
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
              {contactName ? `בכרטיס של ${contactName} ` : "בכרטיס הלקוח "}
              רשומים רק תקציב ואזור, אז אי אפשר לדעת אם הנכס באמת מתאים. השלימו סוג נכס, מספר חדרים ומאפיינים כדי שההתאמה תהיה
              אמינה.
            </Typography>
          </Box>
        </Stack>
      )}

      {/* ONE section for "the customer asked and the listing doesn't deliver", whether the listing
          says it hasn't got it or simply never says — see MatchReasonGroups.missing. The bullets
          still word each case precisely; only the heading stopped asking the realtor to care where
          our information came from. */}
      <Section
        title="חסר בנכס"
        hint="הלקוח ביקש, והנכס לא מספק את זה"
        reasons={groups.missing}
        color="error"
        icon={<CancelRoundedIcon fontSize="small" />}
        compact={compact}
      />
      <Section
        title="שונה ממה שהלקוח ביקש"
        hint="הנכס חורג ממה שרשום בכרטיס"
        reasons={groups.differs}
        color="warning"
        icon={<ErrorOutlineRoundedIcon fontSize="small" />}
        compact={compact}
      />
      <Section
        title="מתאים"
        hint="תואם את מה שרשום בכרטיס"
        reasons={groups.fits}
        color="success"
        icon={<CheckCircleRoundedIcon fontSize="small" />}
        compact={compact}
      />

      {nothingToSay && !groups.dataDepth && (
        <Typography variant="body2" color="text.secondary">
          אין פרטי התאמה לשורה הזו. רעננו את ההתאמות כדי לחשב אותה מחדש.
        </Typography>
      )}

      {/* EXACT here, relative on the row. A relative string answers "is this fresh?" at a glance and
          is right for a cell a realtor scans; it cannot answer "who got this lead first", which is
          the question someone comparing two open rows is asking. The split is the convention written
          out at `crmFormat.ts:114-135`. */}
      {(exactHebrew(createdTime) || exactHebrew(updatedTime)) && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={0.25} sx={{ minWidth: 0 }}>
          {exactHebrew(createdTime) && (
            <Typography variant="caption" color="text.disabled">
              {`נוצר: ${exactHebrew(createdTime)}`}
            </Typography>
          )}
          {exactHebrew(updatedTime) && (
            <Typography variant="caption" color="text.disabled">
              {`עודכן: ${exactHebrew(updatedTime)}`}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
